import type { ErpModule } from '@/api/platform.api';
import { moduleSlug } from '@/config/moduleDefaults';

/** Shared org master-data pages that can be mounted under multiple module prefixes. */
export type SharedMasterPage =
  | 'customers'
  | 'suppliers'
  | 'products'
  | 'inventory'
  | 'inventory/ledger';

/** Modules that expose the shared customers registry under their own URL prefix. */
export const CUSTOMERS_HOST_MODULES: ErpModule[] = [
  'POS',
  'DESK',
  'MARKETING',
  'PROJECTS',
  'LEAD_GEN',
  'CRM',
];

/** Procurement module master-data pages (mirrored from POS catalog/stock routes). */
export const PROCUREMENT_MASTER_PAGES: SharedMasterPage[] = [
  'suppliers',
  'products',
  'inventory',
  'inventory/ledger',
];

const MODULE_SCOPED_MASTER_ROUTE_MAPPINGS: {
  posPath: string;
  page: SharedMasterPage;
  modules: ErpModule[];
}[] = [
  {
    posPath: '/pos/customers',
    page: 'customers',
    modules: CUSTOMERS_HOST_MODULES.filter((m) => m !== 'POS'),
  },
  {
    posPath: '/pos/suppliers',
    page: 'suppliers',
    modules: ['PROCUREMENT'],
  },
  {
    posPath: '/pos/products',
    page: 'products',
    modules: ['PROCUREMENT'],
  },
  {
    posPath: '/pos/inventory',
    page: 'inventory',
    modules: ['PROCUREMENT'],
  },
  {
    posPath: '/pos/inventory/ledger',
    page: 'inventory/ledger',
    modules: ['PROCUREMENT'],
  },
];

export function moduleSharedPath(erpModule: ErpModule, page: SharedMasterPage): string {
  return `/${moduleSlug(erpModule)}/${page}`;
}

export function moduleScopedMasterRoutes(): string[] {
  const routes = new Set<string>();
  for (const mapping of MODULE_SCOPED_MASTER_ROUTE_MAPPINGS) {
    for (const mod of mapping.modules) {
      routes.add(moduleSharedPath(mod, mapping.page));
    }
  }
  return [...routes];
}

/** Copy POS master-data route rules onto module-scoped equivalents. */
export function withModuleScopedMasterRoutes<T>(
  base: Record<string, T>,
): Record<string, T> {
  const expanded = { ...base };
  for (const { posPath, page, modules } of MODULE_SCOPED_MASTER_ROUTE_MAPPINGS) {
    const value = base[posPath];
    if (value === undefined) continue;
    for (const mod of modules) {
      expanded[moduleSharedPath(mod, page)] = value;
    }
  }
  return expanded;
}
